import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  addDays,
  ATTENDANCE_STATES,
  ATTENDANCE_STATE_LABELS,
  eatDayOf,
  formatEatTime,
  formatIsoDay,
} from '@wodi/domain';
import {
  Button,
  Cell,
  Column,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
} from '@wodi/ui';
import { useState } from 'react';

import {
  attendanceReportQuery,
  clockIn,
  clockOut,
  myAttendanceQuery,
  rosterKeys,
  unitsQuery,
} from '../api/roster.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import {
  dateTimeText,
  errorText,
  LoadFailed,
  NotAllowed,
  OfflineListNotice,
} from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { toasts } from '../toasts.ts';
import { dayLabel } from './roster-dialogs.tsx';
import { AttendanceTag } from './words.tsx';

// Attendance (docs/plans/phase3-operations-modules.md §4.3): clocking oneself in and out at
// this facility, and the attendance-against-roster report. Times are stored UTC and shown in
// East Africa Time.

export function MyAttendancePage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const allowed = session !== null && can(session, 'attendance.clock');
  const me = useQuery({ ...myAttendanceQuery, enabled: allowed });
  const clock = useMutation({
    mutationFn: (direction: 'in' | 'out') => (direction === 'in' ? clockIn() : clockOut()),
    onSuccess: (view, direction) => {
      queryClient.setQueryData(rosterKeys.me, view);
      const at = direction === 'in' ? view.open?.clockInAt : view.recent[0]?.clockOutAt;
      toasts.show({
        title: `${direction === 'in' ? 'Clocked in' : 'Clocked out'}${at == null ? '' : ` at ${formatEatTime(new Date(at))}`}`,
      });
    },
  });
  if (!allowed) return <NotAllowed title="My attendance" what="clock in and out" />;
  const open = me.data?.open ?? null;
  return (
    <div className={styles.page}>
      <PageHeader
        title="My attendance"
        description="Clock in when you start and out when you leave."
      />
      <p className={styles.meta}>
        <Link to="/roster">Roster</Link>
      </p>
      {me.isPending ? <LoadingBlock label="Loading your attendance" rows={3} /> : null}
      {me.isError ? (
        <LoadFailed
          error={me.error}
          fallback="Your attendance couldn't be loaded. Try again."
          onRetry={() => void me.refetch()}
        />
      ) : null}
      {me.isSuccess ? (
        <>
          <Section
            title={
              open === null ? 'Not clocked in' : `Clocked in since ${dateTimeText(open.clockInAt)}`
            }
          >
            <div className={styles.form}>
              {clock.isError ? (
                <Notice tone="error">{errorText(clock.error, 'Try again.')}</Notice>
              ) : null}
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  isDisabled={!online}
                  isPending={clock.isPending}
                  pendingLabel="Saving…"
                  onPress={() => {
                    clock.mutate(open === null ? 'in' : 'out');
                  }}
                >
                  {open === null ? 'Clock in' : 'Clock out'}
                </Button>
                {online ? null : <p className={styles.meta}>Clocking needs a connection.</p>}
              </div>
              {me.data.upcoming.length === 0 ? (
                <p className={styles.meta}>You aren&apos;t on a roster here today or tomorrow.</p>
              ) : (
                <ul className={styles.list} aria-label="Your shifts">
                  {me.data.upcoming.map((u) => (
                    <li key={`${u.onDate}-${u.shiftName}-${u.unitName}`} className={styles.item}>
                      {u.shiftName}, {dayLabel(u.onDate)}, {u.unitName}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Section>
          <Section title="Recent" description="Your latest clock-ins, newest first.">
            {me.data.recent.length === 0 ? (
              <p className={styles.meta}>No clock-ins yet.</p>
            ) : (
              <ul className={styles.list} aria-label="Recent clock-ins">
                {me.data.recent.map((r) => (
                  <li key={r.id} className={styles.item}>
                    In {dateTimeText(r.clockInAt)}
                    {r.clockOutAt === null
                      ? ', still clocked in'
                      : `, out ${dateTimeText(r.clockOutAt)}`}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      ) : null}
    </div>
  );
}

export function AttendanceReportPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const units = useQuery(unitsQuery);
  const [now] = useState(() => new Date());
  const today = formatIsoDay(eatDayOf(now));
  const [unit, setUnit] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(formatIsoDay(addDays(eatDayOf(now), -6)));
  const [to, setTo] = useState<string | null>(today);
  const allowed = session !== null && can(session, 'attendance.read');
  const chosen =
    units.data?.items.find((u) => `${u.type}:${u.key}` === unit) ?? units.data?.items[0];
  const range =
    chosen === undefined
      ? null
      : { unitType: chosen.type, unit: chosen.key, from: from ?? today, to: to ?? today };
  const report = useQuery({
    ...attendanceReportQuery(range ?? { unitType: 'ward', unit: '-', from: '', to: '' }),
    enabled: allowed && range !== null,
  });
  if (!allowed)
    return <NotAllowed title="Attendance against roster" what="see the attendance report" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Attendance against roster"
        description="Who came for their rostered shifts. Late and early are 15 minutes either way."
      />
      <p className={styles.meta}>
        <Link to="/roster">Roster</Link>
      </p>
      <OfflineListNotice online={online} />
      <Section title="Report">
        <div className={styles.form}>
          <div className={styles.row}>
            <Select
              label="Ward or department"
              placeholder={units.isPending ? 'Loading…' : 'Choose one'}
              options={(units.data?.items ?? []).map((u) => ({
                id: `${u.type}:${u.key}`,
                label: u.name,
              }))}
              value={chosen === undefined ? null : `${chosen.type}:${chosen.key}`}
              onChange={(key) => {
                setUnit(key === null ? null : String(key));
              }}
              width="lg"
            />
            <DateField label="From" value={from} onChange={setFrom} maxValue={today} />
            <DateField label="To" value={to} onChange={setTo} />
          </div>
          {report.isPending && range !== null ? (
            <LoadingBlock label="Loading the report" rows={5} />
          ) : null}
          {report.isError ? (
            <LoadFailed
              error={report.error}
              fallback="The report couldn't be loaded. Try again."
              onRetry={() => void report.refetch()}
            />
          ) : null}
          {report.isSuccess ? (
            <>
              <p className={styles.meta}>
                {ATTENDANCE_STATES.filter((s) => report.data.counts[s] > 0)
                  .map((s) => `${ATTENDANCE_STATE_LABELS[s]}: ${String(report.data.counts[s])}`)
                  .join(', ') || 'No rostered shifts in this range.'}
              </p>
              <TableScroll>
                <Table flat aria-label="Attendance against roster">
                  <TableHeader>
                    <Column isRowHeader>Person</Column>
                    <Column>Day</Column>
                    <Column>Shift</Column>
                    <Column>In</Column>
                    <Column>Out</Column>
                    <Column>Attendance</Column>
                  </TableHeader>
                  <TableBody
                    items={report.data.rows.map((r) => ({ ...r, id: r.entryId }))}
                    renderEmptyState={() => 'No rostered shifts in this range.'}
                  >
                    {(r) => (
                      <Row id={r.entryId}>
                        <Cell>{r.person.displayName}</Cell>
                        <Cell>{dayLabel(r.onDate)}</Cell>
                        <Cell>{`${r.shiftName}, ${r.startsAt} to ${r.endsAt}`}</Cell>
                        <Cell>
                          {r.clockInAt === null ? 'Not recorded' : dateTimeText(r.clockInAt)}
                        </Cell>
                        <Cell>
                          {r.clockOutAt === null ? 'Not recorded' : dateTimeText(r.clockOutAt)}
                        </Cell>
                        <Cell>
                          <AttendanceTag state={r.state} />
                        </Cell>
                      </Row>
                    )}
                  </TableBody>
                </Table>
              </TableScroll>
              {report.data.unrostered.length === 0 ? null : (
                <>
                  <h3>Clocked in without a rostered shift</h3>
                  <ul className={styles.list} aria-label="Clocked in without a rostered shift">
                    {report.data.unrostered.map((c) => (
                      <li key={c.id} className={styles.item}>
                        {c.person.displayName}, in {dateTimeText(c.clockInAt)}
                        {c.clockOutAt === null ? '' : `, out ${dateTimeText(c.clockOutAt)}`}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : null}
        </div>
      </Section>
    </div>
  );
}
